import { useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Download, Loader2, Send, Sparkles } from 'lucide-react';
import { askInsights, type AskResult } from '@/lib/admin/insights-api';
import { toast } from '@/hooks/use-toast';
import { AskResultView } from './AskResultView';
import { downloadAskPdf } from './askPdf';

type Scope = { orgId: string; surveyId?: string; initialQuestion?: string };

const SURVEY_PROMPTS = [
  'Which anchor has the biggest gap from its target?',
  'Which anchors have declined most in the last month?',
  'Are there any bands where the scores are worse than average?',
  'What themes are coming up in conversations related to this survey?',
];

const ORG_PROMPTS = [
  'How is overall pulse trending this quarter?',
  'Which team has the lowest pulse mean right now?',
  'What themes came up most in Charles conversations this month?',
  'Where should leadership focus attention next week?',
];

export function AskInsightsPanel({ scope }: { scope: Scope }) {
  const [question, setQuestion] = useState(scope.initialQuestion ?? '');
  const [askedQuestion, setAskedQuestion] = useState('');
  const [result, setResult] = useState<AskResult | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const reportRef = useRef<HTMLDivElement>(null);

  const mut = useMutation({
    mutationFn: (q: string) =>
      askInsights({ orgId: scope.orgId, surveyId: scope.surveyId, question: q }),
    onSuccess: (data) => {
      setErrorMsg(null);
      setResult(data);
    },
    onError: (e: Error) => setErrorMsg(e.message || 'Ask failed'),
  });

  const prompts = scope.surveyId ? SURVEY_PROMPTS : ORG_PROMPTS;

  const submit = (q: string) => {
    const trimmed = q.trim();
    if (trimmed.length < 3) return;
    setAskedQuestion(trimmed);
    setQuestion(trimmed);
    mut.mutate(trimmed);
  };

  const handleDownload = async () => {
    if (!reportRef.current || !result) return;
    setDownloading(true);
    try {
      await downloadAskPdf({
        element: reportRef.current,
        filenameSlug: `ask-${askedQuestion}`,
        headerTitle: 'CultureLens — Ask report',
      });
    } catch (e) {
      toast({
        title: 'Could not generate PDF',
        description: e instanceof Error ? e.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-primary" />
          <CardTitle className="text-base font-heading">Ask</CardTitle>
        </div>
        <CardDescription>
          Ask a plain-English question. Answers use only k≥5 aggregated signals — never raw responses.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-2">
          {prompts.map((p) => (
            <Button
              key={p}
              type="button"
              variant="outline"
              size="sm"
              className="text-xs h-auto py-1.5"
              onClick={() => submit(p)}
              disabled={mut.isPending}
            >
              {p}
            </Button>
          ))}
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            submit(question);
          }}
          className="space-y-2"
        >
          <Textarea
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="e.g. Which team has trended down most this month?"
            rows={2}
            maxLength={500}
            disabled={mut.isPending}
          />
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">{question.length}/500</span>
            <Button type="submit" size="sm" disabled={mut.isPending || question.trim().length < 3}>
              {mut.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
              Ask
            </Button>
          </div>
        </form>

        {errorMsg && <p className="text-sm text-destructive">{errorMsg}</p>}

        {result && (
          <div className="space-y-2">
            <div className="flex justify-end">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleDownload}
                disabled={downloading}
              >
                {downloading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Download className="h-4 w-4 mr-2" />
                )}
                Download PDF
              </Button>
            </div>
            <AskResultView ref={reportRef} question={askedQuestion} result={result} />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
